import { Button, Text } from '@kubit-ui-web/react-components';
import {
  findCard,
  nearestCard,
  type Card as DeckCard,
  type Deck,
  type Participant,
  type PublicVote,
  type VoteSummary,
} from '@point-ker/shared';
import { useState } from 'react';

import { Card, Row, Spread, Stack, Columns } from '@/ui/layout';
import { VoteDistribution } from '@/screens/VoteDistribution';

/**
 * How a single person's revealed vote is coloured.
 *
 * - Red marks the extremes — the lowest and the highest card in the room, the
 *   voters most worth hearing before committing.
 * - Green means the room was unanimous: nothing to argue about.
 * - Blue flags a vote the timer filled in because the person did not choose in
 *   time; it takes precedence over the extremes, since "did not vote" is the
 *   more useful thing to surface even though it always lands on the lowest card.
 */
const VOTE_COLOR = {
  extreme: '#c0362c',
  unanimous: '#1a7f37',
  timedOut: '#1f6feb',
} as const;

/**
 * Consolidated results, shown once the host reveals.
 *
 * The numbers are reported exactly as `summarizeVotes` computes them, which
 * means the mean is absent on ordinal decks rather than fabricated, and a
 * split vote shows every tied card instead of silently picking one. Spread is
 * the interesting signal in planning poker — agreement needs no discussion,
 * disagreement does — so unanimity and ties are called out explicitly.
 */
export function ResultsPanel({
  deck,
  results,
  participants,
  votes,
  isHost,
  onCommit,
}: {
  deck: Deck;
  results: VoteSummary;
  participants: readonly Participant[];
  votes: readonly PublicVote[];
  isHost: boolean;
  onCommit: (estimate: string) => void;
}) {
  const meanCard = results.mean === null ? null : nearestCard(deck, results.mean);

  // Pre-select the card nearest the mean rather than the mode: on a numeric
  // deck the average is what the host most often records. The mode-based
  // suggestion is the fallback for ordinal decks, where there is no mean.
  const suggested = meanCard?.label ?? results.suggestion?.label ?? '';
  const [estimate, setEstimate] = useState(suggested);

  const voterRows = buildVoterRows(deck, participants, votes);

  return (
    <Card>
      <Text variant="HEADING_H3_EXPANDED" component="h2">
        Results
      </Text>

      {results.countedVotes === 0 ? (
        <Text variant="PARAGRAPH_MEDIUM_EXPANDED" component="p">
          Nobody cast a countable vote. Talk it through and run the round again.
        </Text>
      ) : (
        <Stack>
          <Row $wrap $gap="1.5rem">
            <Metric label="Median" value={formatNumber(results.median)} />
            {/* Absent by design on t-shirt decks: the average of S and L is
                not a size. */}
            {results.mean !== null && (
              <Metric
                label="Mean"
                value={`${formatNumber(results.mean)}${meanCard ? ` (≈ ${meanCard.label})` : ''}`}
              />
            )}
            <Metric
              label={results.modes.length > 1 ? 'Tied on' : 'Most voted'}
              value={results.modes.map((card) => card.label).join(', ') || '—'}
            />
            <Metric label="Votes" value={String(results.countedVotes)} />
            {results.abstentions > 0 && (
              <Metric label="Abstained" value={String(results.abstentions)} />
            )}
          </Row>

          <Text variant="PARAGRAPH_SMALL_EXPANDED" component="p">
            {results.isUnanimous
              ? 'Unanimous — no discussion needed.'
              : results.modes.length > 1
                ? 'The room is split. Worth hearing the high and low voters before committing.'
                : 'Some spread. The outliers usually know something the rest do not.'}
          </Text>
          <Columns>
            <Row $direction="column" $gap="0.5rem" $align="space-between">
              <Text variant="PARAGRAPH_SMALL_EXPANDED" component="p">
                Distribution
              </Text>
              <VoteDistribution deck={deck} results={results} />
            </Row>
            {voterRows.length > 0 && (
              <Row $direction="column" $gap="0.5rem" $align="space-between">
                <Text variant="PARAGRAPH_SMALL_EXPANDED" component="p">
                  Votes by person
                </Text>
                <Stack $gap="0.35rem">
                  {voterRows.map((row) => (
                    <Spread key={row.participantId}>
                      <Text variant="PARAGRAPH_MEDIUM_EXPANDED" component="strong" color={row.color}>
                        {row.name}
                        {row.timedOut ? ' · NVIT' : ''}
                      </Text>
                      <Text variant="PARAGRAPH_MEDIUM_EXPANDED" component="strong" color={row.color}>
                        {row.cardLabel}
                      </Text>
                    </Spread>
                  ))}
                </Stack>
              </Row>
            )}

          </Columns>
        </Stack>
      )}

      {isHost && (
        <>
          <Row $wrap>
            {/* Pre-filled with the suggestion but freely editable: the room's
                decision outranks the arithmetic. */}
            <Text variant="PARAGRAPH_SMALL_EXPANDED" component="span">
              Record as
            </Text>
            {deck.cards
              .filter((card) => card.value !== null)
              .map((card) => (
                <Button
                  key={card.id}
                  variant={card.label === estimate ? 'PRIMARY' : 'SECONDARY'}
                  size="SMALL"
                  onClick={() => setEstimate(card.label)}
                >
                  {card.label}
                </Button>
              ))}
          </Row>
          <Row>
            <Button
              variant="PRIMARY"
              size="MEDIUM"
              disabled={estimate === ''}
              onClick={() => onCommit(estimate)}
            >
              Save estimate and start next story
            </Button>
          </Row>
        </>
      )}
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Stack $gap="0.15rem">
      <Text variant="PARAGRAPH_CAPTION_EXPANDED" component="span">
        {label}
      </Text>
      <Text variant="HEADING_H3_EXPANDED" component="span">
        {value}
      </Text>
    </Stack>
  );
}

interface VoterRow {
  readonly participantId: string;
  readonly name: string;
  readonly cardLabel: string;
  readonly timedOut: boolean;
  readonly color: string | undefined;
}

/**
 * One row per person who cast a revealed vote, in roster order, each coloured
 * by where it sits in the room (see `VOTE_COLOR`). Spectators and anyone
 * without a revealed card are left out — there is nothing to show for them.
 */
function buildVoterRows(
  deck: Deck,
  participants: readonly Participant[],
  votes: readonly PublicVote[],
): VoterRow[] {
  const cardByParticipant = new Map<string, { card: DeckCard; auto: boolean }>();
  for (const vote of votes) {
    if (vote.cardId === null) continue;
    const card = findCard(deck, vote.cardId);
    if (card) cardByParticipant.set(vote.participantId, { card, auto: vote.auto });
  }

  const rows: { participant: Participant; card: DeckCard; auto: boolean }[] = [];
  for (const participant of participants) {
    if (participant.isSpectator) continue;
    const entry = cardByParticipant.get(participant.id);
    if (entry) rows.push({ participant, ...entry });
  }

  // Extremes are measured over countable cards only; `?`/`☕` have no value and
  // are never the "highest" or "lowest".
  const values = rows.map((row) => row.card.value).filter((value): value is number => value !== null);
  const lowest = values.length > 0 ? Math.min(...values) : null;
  const highest = values.length > 0 ? Math.max(...values) : null;
  const unanimous = lowest !== null && lowest === highest;

  return rows.map((row) => {
    const value = row.card.value;
    let color: string | undefined;
    if (row.auto) color = VOTE_COLOR.timedOut;
    else if (value === null) color = undefined;
    else if (unanimous) color = VOTE_COLOR.unanimous;
    else if (value === lowest || value === highest) color = VOTE_COLOR.extreme;

    return {
      participantId: row.participant.id,
      name: row.participant.name,
      cardLabel: row.card.label,
      timedOut: row.auto,
      color,
    };
  });
}

/** Trims the trailing noise off averages: 10.5 stays, 10.50 and 8.0 do not. */
function formatNumber(value: number | null): string {
  if (value === null) return '—';
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}
